/**
 * FloraFuzzy — client-side fuzzy / typo-tolerant search layer.
 *
 * Loads a lightweight product index from /products.json on first use and
 * exposes window.FloraFuzzy.search(term, limit) -> Promise<Array<Hit>>.
 * Used by predictive-search.js as a fallback when Shopify's native predictive
 * search returns 0 hits (typos like "Baacarat", "Tomf", "Oudh wood" etc).
 *
 * Also exposes a synonyms map that predictive-search uses to expand the
 * native query (e.g. "bacarat" -> "bacarat OR baccarat OR br540").
 */
(function () {
  'use strict';

  var INDEX_URL = '/products.json?limit=250';
  var STORAGE_KEY = 'flora_fuzzy_index_v1';
  var STORAGE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

  var SYNONYMS = {
    'baccarat': ['bacarat', 'bacarrat', 'baccarrat', 'baccarat 540', 'baccarat rouge', 'baccarat rouge 540', 'br540', 'mfk'],
    'maison francis kurkdjian': ['mfk', 'francis kurkdjian', 'kurkdjian'],
    'bleu de chanel': ['blue chanel', 'bleu chanel', 'blue de chanel', 'chanel bleu', 'bleu', 'bleu de'],
    'tom ford': ['tomford', 'tomf', 'tom-ford', 'tf'],
    'oud wood': ['oudwood', 'oudh wood', 'ud wood', 'oud-wood'],
    'tobacco vanille': ['tobaco vanille', 'tobacco vanile', 'tobaco vanile', 'tobacco vanilla'],
    'sauvage': ['sauvag', 'savage', 'savaj', 'dior sauvage'],
    'aventus': ['aventis', 'aventous', 'creed aventus'],
    'jasmine': ['jasmin', 'jasmin sambac', 'jasmime', 'yasmin'],
    'attar': ['ittar', 'itr', 'atar', 'attr'],
    'oud': ['oudh', 'ood', 'agar', 'agarwood'],
    'rose': ['rosa', 'rosee', 'gulab'],
    'musk': ['mushk', 'mosk', 'kasturi'],
    'sandalwood': ['sandal', 'chandan', 'sandlewood'],
    'flora five': ['floraa five', 'flora-5', 'floraf', 'flora 5', 'flora-five'],
    'aatish': ['atish', 'aatishh', 'aatesh'],
    'aab': ['ab', 'aabb'],
    'aagosh': ['agosh', 'aagosh', 'aaghosh'],
    'roll on': ['rollon', 'roll-on', 'rolon'],
    'roll-on': ['rollon', 'roll on', 'rolon']
  };

  var indexPromise = null;
  var fuseInstance = null;
  var products = null;

  function readCache() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.t || !parsed.d) return null;
      if (Date.now() - parsed.t > STORAGE_TTL_MS) return null;
      return parsed.d;
    } catch (_) { return null; }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ t: Date.now(), d: data }));
    } catch (_) { /* quota or disabled, ignore */ }
  }

  /**
   * Strip Shopify body_html down to plain text and pull the first ~400 chars
   * that are useful for matching inspired-by phrases.
   */
  function extractInspiredFromBody(html) {
    if (!html) return '';
    var txt = String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    // Try to capture the "inspired by …" / "interpretation of …" sentence.
    var inspiredHints = [];
    var patterns = [
      /inspired by ([^.]{3,80})/i,
      /interpretation of ([^.]{3,80})/i,
      /accord that defines the [a-z ]*?([A-Z][^.]{3,80})/,
      /reminiscent of ([^.]{3,80})/i,
      /tribute to ([^.]{3,80})/i
    ];
    for (var i = 0; i < patterns.length; i++) {
      var m = txt.match(patterns[i]);
      if (m && m[1]) inspiredHints.push(m[1].trim());
    }
    return inspiredHints.join(' | ') + ' ' + txt.slice(0, 280);
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;

    var cached = readCache();
    if (cached && Array.isArray(cached) && cached.length) {
      products = cached;
      buildFuse(products);
      indexPromise = Promise.resolve(products);
      return indexPromise;
    }

    indexPromise = fetch(INDEX_URL, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (json) {
        var slim = (json.products || []).map(function (p) {
          var inspired = '';
          // Prefer metafield if exposed via the JSON view; otherwise extract from body.
          if (p.metafields && p.metafields.custom && p.metafields.custom.inspired_by) {
            inspired = [].concat(p.metafields.custom.inspired_by).join(' | ');
          } else {
            inspired = extractInspiredFromBody(p.body_html);
          }
          return {
            id: p.id,
            title: p.title || '',
            handle: p.handle || '',
            vendor: p.vendor || '',
            product_type: p.product_type || '',
            tags: (p.tags || []).join(', '),
            inspired: inspired,
            available: p.variants && p.variants.some(function (v) { return v.available; }),
            price: p.variants && p.variants[0] ? p.variants[0].price : '',
            image: p.images && p.images[0] ? p.images[0].src : ''
          };
        });
        products = slim;
        writeCache(slim);
        buildFuse(slim);
        return slim;
      })
      .catch(function () { products = []; buildFuse([]); return []; });

    return indexPromise;
  }

  function buildFuse(list) {
    if (!window.Fuse) return;
    fuseInstance = new window.Fuse(list, {
      includeScore: true,
      shouldSort: true,
      threshold: 0.38,
      distance: 120,
      ignoreLocation: true,
      minMatchCharLength: 2,
      keys: [
        { name: 'title', weight: 0.55 },
        { name: 'inspired', weight: 0.30 },
        { name: 'tags', weight: 0.08 },
        { name: 'vendor', weight: 0.04 },
        { name: 'product_type', weight: 0.03 }
      ]
    });
  }

  function search(term, limit) {
    limit = limit || 6;
    return loadIndex().then(function () {
      if (!fuseInstance || !term) return [];
      // Expand with synonyms so that fuse can match the canonical form too.
      var queries = [term];
      var lower = term.toLowerCase();
      Object.keys(SYNONYMS).forEach(function (canon) {
        var variants = SYNONYMS[canon];
        if (canon.toLowerCase() === lower || variants.some(function (v) { return v.toLowerCase() === lower; })) {
          queries.push(canon);
          variants.forEach(function (v) { queries.push(v); });
        }
      });
      var seen = {};
      var hits = [];
      queries.forEach(function (q) {
        fuseInstance.search(q).forEach(function (r) {
          if (seen[r.item.id]) return;
          seen[r.item.id] = true;
          hits.push(r);
        });
      });
      hits.sort(function (a, b) { return a.score - b.score; });
      // Deprioritise OOS matches.
      hits.sort(function (a, b) {
        if (a.item.available === b.item.available) return 0;
        return a.item.available ? -1 : 1;
      });
      return hits.slice(0, limit).map(function (r) { return r.item; });
    });
  }

  function renderResults(items, term) {
    var lis = items.map(function (p, idx) {
      var img = p.image
        ? '<img class="predictive-search__image w-100" src="' + p.image + '&width=150" alt="" width="50" loading="lazy">'
        : '';
      var inspiredLine = p.inspired
        ? '<small class="predictive-search__inspired d-block" style="opacity:0.7;font-size:0.75rem;line-height:1.2;margin-top:2px;">Inspired by ' + escapeHtml(p.inspired.split('|')[0].trim()) + '</small>'
        : '';
      var oosLine = !p.available
        ? '<small class="predictive-search__oos d-block" style="opacity:0.6;font-size:0.7rem;margin-top:2px;">Out of stock</small>'
        : '';
      return ''
        + '<li id="predictive-search-option-' + (idx + 1) + '" class="predictive-search__list-item" role="option" aria-selected="false" data-product-id="' + p.id + '">'
        +   '<a href="/products/' + encodeURIComponent(p.handle) + '" class="predictive-search__item predictive-search__item--link link--text" tabindex="-1">'
        +     img
        +     '<div class="predictive-search__item-content">'
        +       '<h3 class="predictive-search__item-heading product-card__name">' + escapeHtml(p.title) + '</h3>'
        +       inspiredLine
        +       oosLine
        +     '</div>'
        +   '</a>'
        + '</li>';
    }).join('');

    var dym = items[0] && items[0].title
      ? '<div class="predictive-search__did-you-mean p-2" style="opacity:0.85;font-size:0.85rem;">Did you mean <strong>' + escapeHtml(items[0].title.split(' ').slice(0, 3).join(' ')) + '</strong>?</div>'
      : '';

    return ''
      + dym
      + '<h2 class="predictive-search__heading caption-with-letter-spacing small text-uppercase txt-body-70"><span>Suggestions</span></h2>'
      + '<ul id="predictive-search-results-list" class="predictive-search__results-list list-unstyled" role="listbox">'
      +   lis
      +   '<li class="predictive-search__list-item" role="option" aria-selected="false">'
      +     '<a href="/search?q=' + encodeURIComponent(term) + '&type=product" class="predictive-search__item predictive-search__item--term link link--text">'
      +       'Search the full catalogue for &ldquo;' + escapeHtml(term) + '&rdquo;'
      +     '</a>'
      +   '</li>'
      + '</ul>'
      + '<span class="hide" data-predictive-search-live-region-count-value>(' + items.length + ' suggestions)</span>';
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (ch) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch];
    });
  }

  // Warm the index on first focus of any search input — cheap, single fetch.
  function warmOnFocus() {
    var inputs = document.querySelectorAll('input[type="search"]');
    inputs.forEach(function (el) {
      el.addEventListener('focus', function () { loadIndex(); }, { once: true, passive: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', warmOnFocus);
  } else {
    warmOnFocus();
  }

  window.FloraFuzzy = {
    synonyms: SYNONYMS,
    search: search,
    renderResults: renderResults,
    _loadIndex: loadIndex
  };
})();
